import { sum } from '@front-app/util/math';
import { ref, Ref } from '@vue/composition-api';

export interface UseProgressBar {
	// The current progress as a percentage
	progress: Ref<number>;
}

export interface WeightedPromise {
	promise: Promise<unknown>;
	weight: number;
}

export interface ProgressBarConfig {
	// # of ms to wait before fake incrementing
	timeout: number;
	// The amount to increment (as percentage) by if no change in `timeout` ms
	incrementWeight: number;
	// The max progress to fake (as a percentage)
	maxFakeProgress: number;
}

const defaultConfig: ProgressBarConfig = {
	timeout: 500,
	incrementWeight: 10,
	maxFakeProgress: 90,
};

export const fromWeightedPromises = (weightedPromises: WeightedPromise[], config?: Partial<ProgressBarConfig>): UseProgressBar => {
	const mergedConfig = { ...defaultConfig, ...config };
	const progress = ref(0);
	const total = weightedPromises.map(({ weight }) => weight).reduce(sum, 0);
	const maxFakeTotal = total * (mergedConfig.maxFakeProgress / 100);
	let completedWeight = 0;
	let currentTotal = 0;

	let tid: number;
	const resetTimer = () => {
		clearTimeout(tid);
		tid = window.setTimeout(() => {
			if (progress.value >= mergedConfig.maxFakeProgress || currentTotal > maxFakeTotal) {
				return;
			}
			const incrementWeight = (mergedConfig.incrementWeight / 2) * Math.random() + (mergedConfig.incrementWeight - mergedConfig.incrementWeight / 4);
			currentTotal = Math.min(maxFakeTotal, currentTotal + incrementWeight);
			progress.value = Math.min(currentTotal, maxFakeTotal) / total;
			resetTimer();
		}, ((mergedConfig.timeout / 2) * Math.random() + (mergedConfig.timeout - mergedConfig.timeout / 4)));
	};
	resetTimer();

	const onComplete = (weight: number) => () => {
		resetTimer();
		currentTotal += weight;
		completedWeight += weight;
		if (completedWeight >= total) {
			progress.value = 1;
			clearTimeout(tid);
		} else {
			progress.value = Math.min(currentTotal, maxFakeTotal) / total;
		}
	};
	weightedPromises.forEach(({ promise, weight }) => {
		promise.then(onComplete(weight)).catch(onComplete((weight)));
	});
	return {
		progress,
	};
};

export const fromPromises = (promises: Promise<unknown>[], config?: Partial<ProgressBarConfig>): UseProgressBar => fromWeightedPromises(
	promises.map((promise) => ({ promise, weight: 1 })),
	config,
);
